// src/components/PrivacyAccordion.js
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  ListChecks,
  Scale,
  Activity,
  Shield,
  Clock,
  UserCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";

// Icon-Mapping
function getIcon(icon) {
  const icons = {
    "user-check": <UserCheck className="text-gray-400 w-7 h-7" />,
    "list-checks": <ListChecks className="text-gray-400 w-7 h-7" />,
    "balance-scale": <Scale className="text-gray-400 w-7 h-7" />,
    "activity": <Activity className="text-gray-400 w-7 h-7" />,
    "shield": <Shield className="text-gray-400 w-7 h-7" />,
    "clock": <Clock className="text-gray-400 w-7 h-7" />,
    "check-circle": <CheckCircle className="text-gray-400 w-7 h-7" />,
  };
  return icons[icon] || <CheckCircle className="text-gray-400 w-7 h-7" />;
}

/**
 * Props:
 * - userRole: "mentees" | "mentors"
 * - language: "de" | "en"
 * - onClose: Funktion zum Schließen
 */
export default function PrivacyAccordion({ userRole = "mentees", language = "de", onClose }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [policyData, setPolicyData] = useState([]);
  const [loadError, setLoadError] = useState("");
  const { t } = useTranslation();
  const modalRef = useRef(null);

  // Rollenspezifische Übersetzungs-Keys
  function getRoleKey(baseKey) {
    if (userRole === "mentees") return `${baseKey}Mentee`;
    if (userRole === "mentors") return `${baseKey}Mentor`;
    return baseKey; // fallback
  }

  // Dynamisches Laden der Policy-Datei
  useEffect(() => {
    setPolicyData([]); // leeren beim Wechsel
    setLoadError("");
    import(
      /* webpackChunkName: "policy" */
      `../policies/privacy-policy.${userRole}.${language}.json`
    )
      .then((mod) => setPolicyData(mod.default || mod))
      .catch(() => setLoadError("Datenschutzerklärung konnte nicht geladen werden."));
  }, [userRole, language]);

  // Fokus-Falle & ESC-Handler
  useEffect(() => {
    function trapFocus(e) {
      if (!modalRef.current) return;
      const focusableEls = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
      trapFocus(e);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Setze Fokus auf Modal nach Mount
  useEffect(() => {
    if (modalRef.current) {
      setTimeout(() => {
        const btn = modalRef.current.querySelector("button");
        if (btn) btn.focus();
      }, 100);
    }
  }, []);

  // ---- Übersetzungs-Logik für Header/Button nach Rolle ----
  const headingKey = getRoleKey("privacy.heading");
  const closeKey = getRoleKey("privacy.close");

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-headline"
      >
        {/* Schließen-Button */}
        <button
          className="absolute right-6 top-6 text-gray-400 text-2xl font-bold hover:text-gray-900 transition focus:outline-none"
          onClick={onClose}
          aria-label={t(closeKey)}
        >
          ×
        </button>

        {/* Überschrift */}
        <div className="flex items-center gap-4 mb-4">
          <Shield className="w-9 h-9 text-green-500" />
          <h1
            className="text-3xl font-bold tracking-tight text-green-500 select-none"
            id="privacy-headline"
          >
            {t(headingKey)}
          </h1>
        </div>

        {/* Fehleranzeige */}
        {loadError && (
          <div className="bg-red-100 text-red-700 rounded-lg p-4 mb-4 text-center">
            {loadError}
          </div>
        )}

        {/* Accordion */}
        {!loadError && (
          <div className="flex flex-col gap-3">
            {policyData.map((section, i) => (
              <div
                key={section.id}
                className={
                  "rounded-2xl border transition shadow-sm " +
                  (openIdx === i
                    ? "border-green-500 bg-gray-50 shadow-md"
                    : "border-gray-100 bg-gray-50 hover:shadow-md")
                }
              >
                <button
                  className={
                    "flex w-full items-center gap-4 p-5 text-left focus:outline-none rounded-2xl transition-all duration-150 text-black font-bold " +
                    (openIdx === i
                      ? "bg-white"
                      : "bg-gray-50 hover:bg-gray-100")
                  }
                  onClick={() => setOpenIdx(openIdx === i ? null : i)}
                  aria-expanded={openIdx === i}
                  type="button"
                >
                  {getIcon(section.icon)}
                  <span className="text-xl text-black">{section.title}</span>
                  <span className="ml-auto text-2xl text-gray-400">
                    {openIdx === i ? "–" : "+"}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {openIdx === i && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22 }}
                      className="overflow-hidden px-5 pb-6"
                    >
                      <div className="pt-2 space-y-3 text-gray-700 text-[1.08rem]">
                        {section.content &&
                          section.content.map((item, idx) =>
                            Array.isArray(item) ? (
                              <ul className="list-disc ml-6" key={idx}>
                                {item.map((li, liIdx) => (
                                  <li key={liIdx}>{li}</li>
                                ))}
                              </ul>
                            ) : (
                              <div key={idx}>{item}</div>
                            )
                          )}
                        {section.table && (
                          <div className="overflow-x-auto">
                            <table className="mt-2 w-full border text-gray-700 text-sm">
                              <thead>
                                <tr>
                                  {section.table.headers.map((h, idx) => (
                                    <th
                                      key={idx}
                                      className="p-2 bg-gray-100 font-semibold"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {section.table.rows.map((row, ridx) => (
                                  <tr key={ridx}>
                                    {row.map((cell, cidx) => (
                                      <td
                                        key={cidx}
                                        className="p-2 border-t"
                                      >
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}

        {/* Schließen-Button unten */}
        <div className="flex justify-end pt-8">
          <button
            className="px-6 py-3 rounded-2xl bg-black text-white font-semibold shadow hover:bg-gray-900 transition text-lg"
            onClick={onClose}
            type="button"
          >
            {t(closeKey)}
          </button>
        </div>
      </div>
    </div>
  );
}
